// -- Setters

import { hashAll } from './hash';
import type { Order } from './Order';

export const updateAt = <T>(index: number, value: T, data: T[]): T[] => {
  return data.map((v, i) => (i === index ? value : v));
};

export const insertAt = <T>(index: number, value: T, data: T[]): T[] => {
  return [...data.slice(0, index), value, ...data.slice(index)];
};

// -- Transformers

export const toRecord = <T>(data: T[]): Record<number, T> => {
  return data.reduce((acc, v, i) => ({ ...acc, [i]: v }), {});
};

/**
 *
 * @description Stack-safe version of Math.min that leverages reusable Order instances
 */
export const min = <T>(data: T[], order: Order<T>): T | undefined => {
  return data.reduce(
    (acc, v) => (acc === undefined || order.compare(v, acc) < 0 ? v : acc),
    undefined as T | undefined
  );
};

/**
 *
 * @description Stack-safe version of Math.max that leverages reusable Order instances
 */
export const max = <T>(data: T[], order: Order<T>): T | undefined => {
  return data.reduce(
    (acc, v) => (acc === undefined || order.compare(v, acc) > 0 ? v : acc),
    undefined as T | undefined
  );
};

// -- iterators

export const mapPairWise = <T, U>(
  data: T[],
  fn: (a: T, b: T, i: number) => U
): U[] => {
  return data.slice(1).map((v, i) => fn(data[i - 1]!, v, i));
};

export const findMap = <T, U>(
  data: T[],
  pred: (v: T) => boolean,
  fn: (v: T) => U
): U | undefined => {
  const item = data.find(pred);
  return item ? fn(item) : undefined;
};

export const groupBy = <T, U extends string | number | symbol>(
  data: T[],
  fn: (v: T) => U
): Record<U, T[]> => {
  const acc = {} as Record<U, T[]>;
  data.forEach((v) => {
    const key = fn(v);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(v);
  });
  return acc;
};

export const mapFilter = <T, U>(
  data: T[],
  fn: (v: T) => U | undefined
): U[] => {
  return data.map(fn).filter((v) => v !== undefined) as U[];
};

export const mapFind = <T, U>(
  data: T[],
  fn: (v: T) => U | undefined
): U | undefined => {
  return data.map(fn).find((v) => v !== undefined);
};

export const map = <T, U>(data: T[], fn: (v: T) => U): U[] => {
  return data.map(fn);
};

export const unique = <T>(data: T[]): T[] => {
  const acc = [] as T[];
  const seen = new Set();
  data.forEach((v) => {
    if (!seen.has(hashAll(v))) {
      acc.push(v);
      seen.add(hashAll(v));
    }
  });
  return acc;
};

export const uniqueBy = <T, U>(data: T[], fn: (v: T) => U): T[] => {
  const acc = [] as T[];
  const seen = new Set();
  data.forEach((v) => {
    const key = hashAll(fn(v));
    if (!seen.has(key)) {
      acc.push(v);
      seen.add(key);
    }
  });
  return acc;
};

export const sort = <T>(data: T[], order: Order<T>): T[] => {
  return data.slice().sort((a, b) => order.compare(a, b));
};

// -- comparators

/**
 *
 * @description Check if all elements in the array are equal after applying the function to each element.
 * @example
 * const data = [[0, 1], [0, 2], [0, 3]];
 * mapEqual(data, ([a, b]) => a); // keeps only the first element of each pair, which is always 0 -> true
 * mapEqual(data, ([a, b]) => b); // keeps only the second element of each pair, which are all different -> false
 */
export const mapEqual = <T, U>(data: T[], fn: (a: T) => U): boolean => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return data.slice(1).every((v, i) => fn(data[i]!) === fn(v));
};
